import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

import { NotificationDismissReason } from '../models/notification.enum';
import { NotificationInstance } from '../models/notification.model';
import { toastAnimation, ToastAnimationParams, toastAnimationParams } from '../notification.animations';
import {
  NOTIFICATION_CLOSE_ICON,
  NOTIFICATION_ICON_NAMESPACE,
  notificationIconName,
  registerNotificationIcons,
} from '../notification-icons';

/** Emitted when a toast asks to be taken off screen. */
export interface NotificationDismissEvent {
  id: string;
  reason: NotificationDismissReason;
}

/**
 * A single toast. Purely presentational apart from owning its own auto-dismiss
 * timer — it never mutates the stack, it only reports that it wants to close.
 *
 * Not intended for direct use; raise notifications through `NotificationService`.
 */
@Component({
    selector: 'du-notification-toast',
    imports: [CommonModule, MatIconModule],
    templateUrl: './notification-toast.component.html',
    styleUrls: ['./notification-toast.component.scss'],
    animations: [toastAnimation],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationToastComponent implements OnInit, OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private initialised = false;
  private timerHandle: ReturnType<typeof setTimeout> | null = null;
  /** Wall-clock deadline for the running timer; drives pause/resume maths. */
  private expiresAt = 0;
  private remaining = 0;
  private closing = false;

  private toast!: NotificationInstance;

  @Input()
  set notification(value: NotificationInstance) {
    this.toast = value;
    // A repeat of a de-duplicated toast arrives as a new object on the same id;
    // treat it as "seen again" and give the user the full duration once more.
    if (this.initialised) {
      this.restartTimer();
    }
  }
  get notification(): NotificationInstance {
    return this.toast;
  }

  /** Accessible name for the × button, supplied by the container's defaults. */
  @Input() closeAriaLabel = 'Dismiss notification';

  @Output() readonly dismissed = new EventEmitter<NotificationDismissEvent>();
  @Output() readonly actionInvoked = new EventEmitter<NotificationInstance>();

  readonly closeIcon = `${NOTIFICATION_ICON_NAMESPACE}:${NOTIFICATION_CLOSE_ICON}`;

  constructor() {
    registerNotificationIcons(inject(MatIconRegistry), inject(DomSanitizer));
  }

  @HostBinding('class')
  get hostClasses(): string {
    const extra = this.toast.panelClass;
    const custom = !extra ? [] : Array.isArray(extra) ? extra : [extra];
    return ['du-toast', `du-toast--${this.toast.type}`, ...custom].join(' ');
  }

  @HostBinding('attr.role')
  get roleAttr(): string {
    return this.toast.role;
  }

  /**
   * The stack announces new toasts through the CDK `LiveAnnouncer`, whose live
   * region is already in the DOM when the text lands — a region created in the
   * same frame as its content is announced unreliably. Silencing the implicit
   * `assertive` that `role="alert"` carries keeps that from double-announcing.
   */
  @HostBinding('attr.aria-live')
  readonly ariaLiveAttr = 'off';

  @HostBinding('attr.aria-atomic')
  readonly ariaAtomicAttr = 'true';

  /**
   * Reachable by keyboard so Escape can close it and so the stack is
   * discoverable while tabbing, without stealing focus on arrival.
   */
  @HostBinding('attr.tabindex')
  readonly tabIndexAttr = '0';

  @HostBinding('@toastAnimation')
  get animation(): ToastAnimationParams {
    return toastAnimationParams(this.toast.position);
  }

  /** Honours the OS "reduce motion" setting by skipping the slide entirely. */
  @HostBinding('@.disabled')
  readonly animationsDisabled =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Registered SVG glyph for this severity, ignored when a custom icon is set. */
  get severityIcon(): string {
    return `${NOTIFICATION_ICON_NAMESPACE}:${notificationIconName(this.toast.type)}`;
  }

  get showIcon(): boolean {
    return this.toast.icon !== null;
  }

  ngOnInit(): void {
    this.initialised = true;
    this.startTimer(this.toast.duration);
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  /** Gives the toast keyboard focus — used when a caller wants to force attention. */
  focus(): void {
    this.host.nativeElement.focus({ preventScroll: true });
  }

  onAction(event: Event): void {
    event.stopPropagation();
    const action = this.toast.action;
    if (!action) {
      return;
    }

    action.handler?.();
    this.actionInvoked.emit(this.toast);

    if (action.dismissOnAction !== false) {
      this.close(NotificationDismissReason.Action);
    }
  }

  onClose(event: Event): void {
    event.stopPropagation();
    this.close(NotificationDismissReason.User);
  }

  @HostListener('click')
  onHostClick(): void {
    if (this.toast.tapToDismiss) {
      this.close(NotificationDismissReason.User);
    }
  }

  @HostListener('keydown.escape', ['$event'])
  onEscape(event: Event): void {
    // Scoped to the focused toast, so Escape still reaches dialogs behind it.
    event.stopPropagation();
    this.close(NotificationDismissReason.User);
  }

  @HostListener('mouseenter')
  @HostListener('focusin')
  onInteractionStart(): void {
    if (this.toast.pauseOnHover) {
      this.pauseTimer();
    }
  }

  @HostListener('mouseleave')
  onPointerLeave(): void {
    if (this.toast.pauseOnHover) {
      this.resumeTimer();
    }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    // Ignore focus moving between the toast's own buttons.
    if (this.toast.pauseOnHover && !(next && this.host.nativeElement.contains(next))) {
      this.resumeTimer();
    }
  }

  private close(reason: NotificationDismissReason): void {
    if (this.closing) {
      return;
    }
    this.closing = true;
    this.clearTimer();
    this.dismissed.emit({ id: this.toast.id, reason });
  }

  private restartTimer(): void {
    this.clearTimer();
    this.startTimer(this.toast.duration);
  }

  private startTimer(duration: number): void {
    if (!duration || duration <= 0) {
      return;
    }

    this.remaining = duration;
    this.expiresAt = Date.now() + duration;

    // Kept out of the zone so a stack of pending toasts does not schedule a
    // change-detection pass per tick while it sits idle on screen.
    this.zone.runOutsideAngular(() => {
      this.timerHandle = setTimeout(() => {
        this.timerHandle = null;
        this.zone.run(() => this.close(NotificationDismissReason.Timeout));
      }, duration);
    });
  }

  private pauseTimer(): void {
    if (this.timerHandle === null) {
      return;
    }
    this.remaining = Math.max(0, this.expiresAt - Date.now());
    this.clearTimer();
  }

  private resumeTimer(): void {
    if (this.timerHandle !== null || this.closing || this.remaining <= 0) {
      return;
    }
    this.startTimer(this.remaining);
  }

  private clearTimer(): void {
    if (this.timerHandle !== null) {
      clearTimeout(this.timerHandle);
      this.timerHandle = null;
    }
  }
}
